import type { LocalDatabaseProduct } from "../../../Data/_Bases/BaseProductRepository";
import type {
    LocalDatabasePromotion,
    LocalDatabasePromotionCriteria,
} from "../../../Data/_Bases/BasePromotionRepository";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { PromotionManager } from "./PromotionManager";
import type { GroupedProducts } from "./PromotionUtilities";

/*
 * The following are the rules of rebates in Shopfront:
 *
 * - Rebates are defined at the quantity of the promotion
 * - Pick the highest rebate when within the same criteria
 * - Rebates applied directly to products should take priority (even if lower)
 * - More than should apply the rebate at the same rate
 * - When across multiple criteria, select the highest rebate
 * - Apply rebates no matter the sell price
 * - No stacking of rebates (select the highest rebate, use the Knapsack calculation to determine things like
 *   4 exact + 1 exact and buy 5 get all rebates)
 * - Mix criteria should take the percentage of the promotion
 * - More than with a mix criteria should work like a normal more than (at the same rate)
 * - If you have a 2 or more + a 2 exact and buy 2 + 1 you should get the rebate for all three
 * - Activation required promotions should only rebate when the activation is active
 */

type RebateResult = Record<string, {
    quantity: number;
    rebate: number;
    rate: number;
    moreThan: boolean;
}>;

export class RebateCalculation {
    protected isMixingCriteria: boolean;

    constructor(
        protected manager: PromotionManager,
        protected promotion: LocalDatabasePromotion,
        protected products: GroupedProducts,
        protected databaseProducts: Record<string, LocalDatabaseProduct>
    ) {
        // To fix old invalid promotions, in the future it would be good to remove this
        this.isMixingCriteria = this.promotion.mix_criteria && this.promotion.criteria.length === 2;
    }

    /**
     * Get the promotional rebate for the product in the provided criteria
     */
    protected getProductRebateForCriteria(
        product: string,
        criteria: LocalDatabasePromotionCriteria
    ): number | null {
        // Get the repository product
        const dbProduct = this.databaseProducts[product];

        // First, check if the product is included
        for(const product of criteria.products) {
            // Rule: Rebates applied directly to products should take priority (even if lower)
            if(product.uuid === dbProduct.uuid) {
                return product.rebate;
            }
        }

        // Check if the product should be excluded
        if(dbProduct.brand_id && criteria.excludes.brands.includes(dbProduct.brand_id)) {
            // No rebate available
            return null;
        }

        if(dbProduct.category_id && criteria.excludes.categories.includes(dbProduct.category_id)) {
            // No rebate available
            return null;
        }

        if(dbProduct.family_id && criteria.excludes.families.includes(dbProduct.family_id)) {
            // No rebate available
            return null;
        }

        if(criteria.excludes.products.includes(dbProduct.uuid)) {
            // No rebate available
            return null;
        }

        for(const dbTag of dbProduct.tags) {
            if(criteria.excludes.tags.includes(dbTag)) {
                // No rebate available
                return null;
            }
        }

        // Check if the product should be included
        // Rule: Pick the highest rebate when within the same criteria
        let rebate = 0;

        for(const brand of criteria.brands) {
            if(brand.uuid === dbProduct.brand_id) {
                if(rebate < brand.rebate) {
                    rebate = brand.rebate;
                }

                break;
            }
        }

        for(const category of criteria.categories) {
            if(category.uuid === dbProduct.category_id) {
                if(rebate < category.rebate) {
                    rebate = category.rebate;
                }

                break;
            }
        }

        for(const family of criteria.families) {
            if(family.uuid === dbProduct.family_id) {
                if(rebate < family.rebate) {
                    rebate = family.rebate;
                }

                break;
            }
        }

        for(const tag of criteria.tags) {
            for(const dbTag of dbProduct.tags) {
                if(tag.uuid === dbTag) {
                    if(rebate < tag.rebate) {
                        rebate = tag.rebate;
                    }
                }
            }
        }

        if(!rebate) {
            // Using the rebate as a fallback instead of a comparison (from SHOPFRONT-9052)
            if(typeof criteria.allItems === "number") {
                return criteria.allItems;
            }

            return null;
        }

        return rebate;
    }

    /**
     * Determine the quantity being applied to the criteria from the sale
     */
    protected getQuantityPerCriteria(rebatesPerCriteria: Record<string, Array<number | null>>): Array<number> {
        const quantityPerCriteria = ArrayPool.Get<number>();

        for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
            let quantityInSale = 0;

            for(const product in this.products) {
                if(!Object.hasOwn(this.products, product)) continue;

                if(typeof rebatesPerCriteria[product]?.[i] === "undefined") continue;

                if(rebatesPerCriteria[product][i] === null) continue;

                quantityInSale += this.products[product].total_quantity;
            }

            quantityPerCriteria.push(quantityInSale);
        }

        return quantityPerCriteria;
    }

    /**
     * Check the promotion is valid for applying rebates
     */
    protected checkPromotionValidity(quantityPerCriteria: Array<number>): boolean {
        for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
            if(this.promotion.criteria[i].optional) continue;

            if(
                (quantityPerCriteria[i] ?? 0) < this.promotion.criteria[i].buy_amount &&
                !this.isMixingCriteria
            ) {
                return false;
            } else if(this.isMixingCriteria) {
                return true;
            }
        }

        return true;
    }

    /**
     * Obtain the rebate for each product per criteria
     */
    protected getRebatesPerCriteria(product: string): Array<number | null> {
        const rebates = ArrayPool.Get<number | null>();

        if(typeof this.databaseProducts[product] === "undefined") {
            return rebates;
        }

        for(const criteria of this.promotion.criteria) {
            rebates.push(this.getProductRebateForCriteria(product, criteria));
        }

        return rebates;
    }

    /**
     * When not mixing criteria, determine the maximum number of times we can apply the promotion
     */
    protected getMaxPromotionSets(quantityPerCriteria: Array<number>): number {
        let maxCompleteSets = Infinity;

        for(let i = 0; i < this.promotion.criteria.length; i++) {
            const criteria = this.promotion.criteria[i];

            if(criteria.optional) {
                continue; // Skip optional criteria when calculating sets
            }

            if(criteria.quantity_type === "more") {
                continue; // More than criteria shouldn't count towards the max
            }

            // Calculate how many complete sets this criteria can support
            const possibleSets = Math.floor(quantityPerCriteria[i] / criteria.buy_amount);

            maxCompleteSets = Math.min(maxCompleteSets, possibleSets);
        }

        return maxCompleteSets;
    }

    /**
     * When mixing criteria, determine the maximum number of times the criteria can be mixed
     */
    protected getMixCriteriaMultiplier(quantityPerCriteria: Array<number>): Array<number> {
        const quantityInSaleForCriteriaOne = quantityPerCriteria[0];
        const quantityInSaleForCriteriaTwo = quantityPerCriteria[1];

        const buyAmountCriteriaOne = this.promotion.criteria[0].buy_amount;
        const buyAmountCriteriaTwo = this.promotion.criteria[1].buy_amount;

        const ratio1 = quantityInSaleForCriteriaOne / buyAmountCriteriaOne;
        const ratio2 = quantityInSaleForCriteriaTwo / buyAmountCriteriaTwo;

        const multipliers = ArrayPool.Get<number>();

        if(ratio1 + ratio2 < 1) {
            multipliers[0] = 0;
            multipliers[1] = 0;
        } else {
            multipliers[0] = ratio1;
            multipliers[1] = ratio2;
        }

        return multipliers;
    }

    /**
     * Determine the rebates for a specific criteria
     */
    protected calculateRebatesForCriteria(
        criteria: LocalDatabasePromotionCriteria,
        quantity: number,
        rebate: number | null,
        multiplier: number,
        maxSets: number
    ): Array<number> {
        const rebates = ArrayPool.Get<number>();

        // Determine which buy amount to use, ensuring we handle mixed criteria correctly
        let effectiveBuyAmount = criteria.buy_amount * multiplier;

        if(rebate === null || quantity < effectiveBuyAmount) {
            // Return an empty array, we handle this outside the function
            return rebates;
        }

        // Determine the rebate we would receive per-item
        const rebatePerItem = rebate / criteria.buy_amount;

        // We use a windowing method to determine how to apply the quantity.
        // For example, we can have this applying to quantities between 4 & 6, so the window needs to ignore
        // 1, 2, 3, 7 but include 8
        const windowSize = (
            criteria.quantity_type === "more" ?
                (criteria.buy_amount_max || Infinity) :
                criteria.buy_amount
        );

        // Rule: If you have a 2 or more + a 2 exact and buy 2 + 1, you should get the rebate for all three
        // We also decided to not treat criteria with a max buy amount with the same rule to ensure you can't get odd
        // amounts with it
        if(windowSize === Infinity) {
            effectiveBuyAmount = 1;
        }

        // Loop through the quantity and calculate the correct value for the rebate
        for(let i = 0; i < quantity; i++) {
            const currentQuantity = i + 1;

            if(currentQuantity > effectiveBuyAmount * maxSets) {
                rebates[i] = rebates[i - 1] ?? 0;
            } else if(currentQuantity < effectiveBuyAmount) {
                rebates[i] = 0;
            } else if(windowSize === Infinity) {
                rebates[i] = rebatePerItem * currentQuantity;
            } else {
                const completeWindows = Math.floor(currentQuantity / windowSize);
                const remainderInCurrentWindow = currentQuantity % windowSize;

                let totalRebate = completeWindows * (rebatePerItem * windowSize);

                if(remainderInCurrentWindow >= effectiveBuyAmount) {
                    totalRebate += rebatePerItem * remainderInCurrentWindow;
                }

                rebates[i] = totalRebate;
            }
        }

        return rebates;
    }

    /**
     * Fill the matrix of rebates for each product and each criteria to use in comparisons later
     */
    protected fillMatrix(
        matrix: Record<string, Array<number>>,
        rebatesPerCriteria: Record<string, Array<number | null>>,
        quantityPerCriteria: Array<number>,
        multiplier: Array<number> | undefined,
        maxSetsToApply: number
    ): void {
        for(const product in this.products) {
            for(let i = 0, l = this.promotion.criteria.length; i < l; i++) {
                if(quantityPerCriteria[i] === 0) continue;

                if(typeof multiplier !== "undefined" && multiplier[i] === 0) {
                    continue;
                }

                const criteria = this.promotion.criteria[i];
                const quantityToApply = Math.max(quantityPerCriteria[i], this.products[product].total_quantity);

                const rebates = this.calculateRebatesForCriteria(
                    criteria,
                    quantityToApply,
                    rebatesPerCriteria[product]?.[i] ?? null,
                    multiplier?.[i] ?? 1,
                    maxSetsToApply
                );

                if(rebates.length === 0) continue;

                // Add the rebates to the matrix
                for(let j = 0; j < quantityToApply; j++) {
                    if(typeof matrix[product][j] === "undefined") {
                        matrix[product][j] = rebates[j] ?? 0;
                    } else {
                        matrix[product][j] = Math.max(rebates[j] ?? 0, matrix[product][j]);
                    }
                }
            }
        }
    }

    /**
     * Obtain the rebate matrix for comparison
     */
    protected getRebateMatrix(): Record<string, Array<number>> {
        const matrix = ObjectPool.Get<Record<string, Array<number>>>();
        const rebatesPerCriteria = ObjectPool.Get<Record<string, Array<number | null>>>();

        for(const product in this.products) {
            if(!Object.hasOwn(this.products, product)) continue;

            matrix[product] = ArrayPool.Get<number>();

            for(let i = 0; i < this.products[product].total_quantity; i++) {
                matrix[product].push(0);
            }

            rebatesPerCriteria[product] = this.getRebatesPerCriteria(product);
        }

        const quantityPerCriteria = this.getQuantityPerCriteria(rebatesPerCriteria);

        if(!this.checkPromotionValidity(quantityPerCriteria)) {
            return matrix;
        }

        let multiplier: Array<number> | undefined;
        let maxSetsToApply = Infinity;

        if(this.isMixingCriteria) {
            multiplier = this.getMixCriteriaMultiplier(quantityPerCriteria);
        } else {
            maxSetsToApply = this.getMaxPromotionSets(quantityPerCriteria);
        }

        if(maxSetsToApply > 0) {
            this.fillMatrix(
                matrix,
                rebatesPerCriteria,
                quantityPerCriteria,
                multiplier,
                maxSetsToApply
            );
        }

        ArrayPool.Release(quantityPerCriteria);
        ObjectPool.Release(rebatesPerCriteria);

        if(typeof multiplier !== "undefined") {
            ArrayPool.Release(multiplier);
        }

        return matrix;
    }

    public runMatrix(): Record<string, Array<number>> {
        return this.getRebateMatrix();
    }

    /**
     * Run the rebate calculation in the context of a single promotion
     */
    public run(): RebateResult {
        /*
         * For information on the structure of the promotion and what the expected outcome is, check `RebateRules.txt`
         */
        const matrix = this.getRebateMatrix();
        const result = ObjectPool.Get<RebateResult>();

        for(const product in matrix) {
            if(!Object.hasOwn(matrix, product)) continue;

            result[product] = ObjectPool.Get();

            result[product].quantity = this.products[product].total_quantity;
            result[product].rebate = matrix[product][this.products[product].total_quantity - 1];
            result[product].rate = result[product].rebate / result[product].quantity;
            result[product].moreThan = true;
        }

        ObjectPool.Release(matrix);

        return result;
    }
}
